/** IXD 편집기 - 상단 바(공용 MainHeader)와 파일 정보 줄(공용 SubHeader) */

import {
  Download,
  FolderOpen,
  Loader2,
  RotateCcw,
  Save,
  Undo2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AppMainHeader } from "@/components/app-shell";
import type { DocumentSidebarToggleState } from "@/components/document-sidebar";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import {
  ViewSwitchGroup,
  type ViewSwitchOption,
} from "@/toggle-ui-kit/view-switch-group";
import {
  SubHeader,
  SubHeaderNote,
  type SubHeaderFile,
} from "@/toggle-ui-kit/sub-header";
import { Switch } from "@/toggle-ui-kit/control/switch";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import { useIxdEditorStore } from "./store";
import { pushModal } from "@/stores/modal";
import type { FsType } from "@/apis/ixd-editor/types";
import {
  getSaveButtonState,
  type useIxdEditSession,
} from "@/hooks/ixd-edit-session";
import {
  SaveRejectionPopover,
  type SaveRejectionPopoverProps,
} from "@/components/ixd-workbook-match";
import { ResetChangesConfirmModal } from "@/components/modals/reset-changes-confirm-modal";
import { SaveChangesConfirmModal } from "@/components/modals/save-changes-confirm-modal";
import { guardUnsavedExit } from "@/components/modals/unsaved-exit-confirm-modal";
import type { ReportType } from "@/components/common/report-chip";
import type { DsdFile } from "@/types/dsd";
import { takeWorkbookLoadGuideView } from "@/utils/ixd-workbook-match/integration-guide";
import { SUB_HEADER_ROW_PX } from "./sub-header-collapse";
import { logIxdEditorAction } from "@/utils/usage-log";
import { WorkbookValueLoadModal } from "./workbook-value-load-modal";

const FS_TYPE_OPTIONS: ViewSwitchOption<FsType>[] = [
  { value: "consolidated", label: "연결" },
  { value: "separate", label: "별도" },
];

// 조사(을/를)가 갈려 템플릿 대신 문장을 그대로 둔다.
const NOT_AUTHORED_TOOLTIP: Record<FsType, string> = {
  consolidated: "이 문서는 연결을 작성하지 않았습니다",
  separate: "이 문서는 별도를 작성하지 않았습니다",
};

type EditSession = ReturnType<typeof useIxdEditSession>;

interface EditorHeaderProps {
  session: EditSession;
  /** 이 문서가 실제로 담은 유형. 한쪽만 담은 IXD 에서 빈 탭을 내보이지 않는다 */
  availableFsTypes: FsType[];
  /** 목차 펼침·hover 미리보기 — 상태는 페이지가 들고 토글만 여기 붙는다 */
  sidebar: DocumentSidebarToggleState["headerToggle"];
  /** 되돌리기. 표 안 Ctrl+Z 와 같은 핸들러를 쓴다 (피드백·fsType 전환 포함) */
  onUndo: () => void;
  /** 저장되지 않은 값 안내. 고칠 것이 남아 있는 동안만 전달된다. */
  rejectionGuide?: SaveRejectionPopoverProps | null;
}

export const EditorHeader = ({
  session,
  availableFsTypes,
  sidebar,
  onUndo,
  rejectionGuide,
}: EditorHeaderProps) => {
  const selectedFsType = useIxdEditorStore((s) => s.selectedFsType);
  const setSelectedFsType = useIxdEditorStore((s) => s.setSelectedFsType);
  const navigate = useNavigate();
  // 저장 후 백그라운드 재조회 중에도 잠긴다 — 무거운 재조회가 중복 발생하지 않게.
  const isBusy = session.isActionLocked;
  const saveButton = getSaveButtonState(
    session.saveInFlight,
    session.isReflecting,
  );
  return (
    <AppMainHeader
      sidebar={sidebar}
      // 이 화면의 유일한 앱 내 이탈 경로 (탭 닫기·새로고침은 useUnsavedChangesGuard 가 맡는다)
      onLogoClick={() =>
        guardUnsavedExit(session, () => {
          logIxdEditorAction("로고 클릭으로 나가기");
          navigate("/");
        })
      }
      center={
        // Figma mainHeader(7145:451431) 의 View Switch Group — 컨테이너 36px = 패딩 4 + 항목 28
        <ViewSwitchGroup<FsType>
          // 문서에 없는 유형도 자리는 남기고 잠근다 — 탭이 사라지면 "원래 없는 화면"
          // 처럼 보여서, 왜 못 고르는지를 tooltip 으로 말해 준다.
          options={FS_TYPE_OPTIONS.map((option) =>
            availableFsTypes.includes(option.value)
              ? option
              : {
                  ...option,
                  disabled: true,
                  tooltip: NOT_AUTHORED_TOOLTIP[option.value],
                },
          )}
          value={selectedFsType}
          onChange={(fsType) => {
            // URL 의 fsType 은 전환 뒤에 바뀐다 — 전환할 쪽을 직접 실어야 로그가 맞다
            logIxdEditorAction(
              fsType === "consolidated" ? "연결 보기" : "별도 보기",
              { fsType },
            );
            setSelectedFsType(fsType);
          }}
          size="28px"
        />
      }
      actions={
        <>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            leftIcon={<RotateCcw className="w-3.5 h-3.5" />}
            // 되돌리기 버튼이 바로 옆이고 초기화는 undo 스택까지 비워 복구가 안 된다 — 워크북 매칭 헤더와 같은 확인 모달을 거친다
            onClick={() => {
              logIxdEditorAction("전체 초기화");
              pushModal(ResetChangesConfirmModal, {
                onConfirm: () => {
                  logIxdEditorAction("전체 초기화 확정");
                  session.resetAllChanges();
                  toast.success("모든 수정 사항이 초기화되었습니다.");
                },
              });
            }}
            disabled={!session.hasUnsavedChanges || isBusy}
          >
            전체 초기화
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            // 표 안 Ctrl+Z 는 세지 않는다 — 버튼을 찾아 눌렀는지를 본다
            onClick={() => {
              logIxdEditorAction("되돌리기");
              onUndo();
            }}
            disabled={session.undoCount === 0 || isBusy}
            leftIcon={<Undo2 className="w-3.5 h-3.5" />}
          >
            되돌리기
          </Button>
          {/* z-[60]: 본문(비교 표 sticky 제목 z-50)이 DOM 상 뒤라 같은 z 로는 안내가 가린다.
              안내가 없을 때까지 올리면 저장·다운로드 중 busy 오버레이(editor-content-view 의
              absolute inset-0 z-50)가 이 버튼을 덮지 못한다.
              모달·다운로드 완료 패널(Sheet) 같은 body 포털과의 관계는 여기서 신경 쓰지 않는다 —
              EditorContentView 루트의 `isolate` 가 이 페이지의 z 를 전부 가둬 포털이 항상 위다. */}
          <div className={rejectionGuide ? "relative z-[60]" : "relative"}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              // WXBRL-2660: "저장 버튼 항시 enabled, 수정된 값 없으면 아무 동작도 안함" 이 티켓의
              // 요구사항이다. 그래서 toast 안내조차 의도적으로 띄우지 않는다 (버그 아님).
              onClick={() => {
                // 수정이 없으면 아무 동작도 안 하는 클릭(WXBRL-2660)도 세되, 집계에서 나눌 수 있게 둔다
                logIxdEditorAction("저장", {
                  hasUnsavedChanges: session.hasUnsavedChanges,
                });
                if (!session.hasUnsavedChanges) return;
                pushModal(SaveChangesConfirmModal, {
                  // 저장 결과 안내는 세션 훅이 한다 (use-ixd-edit-session saveMutation)
                  onConfirm: () => {
                    logIxdEditorAction("저장 확정");
                    session.saveMutation.mutate();
                  },
                });
              }}
              disabled={isBusy}
              leftIcon={
                saveButton.inProgress ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )
              }
            >
              {saveButton.label}
            </Button>
            {rejectionGuide && <SaveRejectionPopover {...rejectionGuide} />}
          </div>
          <Button
            // Figma mainHeader 의 CTA 는 파란 solid (Primary/Color) — solid primary 는 검정이다
            variant="solid"
            color="blue"
            size="small"
            onClick={() => {
              logIxdEditorAction("IXD로 다운로드");
              session.downloadMutation.mutate();
            }}
            disabled={isBusy}
            leftIcon={
              session.downloadInFlight ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )
            }
          >
            IXD로 다운로드
          </Button>
        </>
      }
    />
  );
};

/**
 * 상단 바 아래 파일 정보 줄. 목차 오른쪽 본문 컬럼 위에만 놓인다 (Figma subHeader).
 *
 * 워크북을 고르면 `IXD ↔ DSD 워크북` 두 칸이 되고, "DSD워크북 보기"를 끄면
 * 본문의 워크북 열과 함께 이 줄에서도 워크북 칸이 사라진다 (Figma 7145:466888).
 */
/**
 * 워크북을 막 불러왔을 때 DSD 워크북 파일명 아래에 띄우는 안내 (Figma 7246:577434).
 *
 * hover 툴팁이 아니라 한 번만 보여 주는 안내라 Radix Tooltip 대신 파일명 기준
 * 절대 위치로 둔다 — 화살표가 파일명 시작점에 붙어야 해서 trigger 중앙에
 * 화살표를 두는 Radix 배치와 맞지 않는다.
 */
const WorkbookLoadedHint = () => (
  <div
    role="status"
    // z-[60]: 본문(비교 표 sticky 제목 z-50)이 DOM 상 뒤라 같은 z 로는 안내가 가린다.
    // pointer-events-none: 클릭 한 번으로 닫히는 안내라 아래 요소의 클릭을 막지 않는다.
    className="pointer-events-none absolute left-0 top-full z-[60] pt-[3px] whitespace-nowrap"
  >
    <svg
      aria-hidden
      width="10"
      height="6"
      viewBox="0 0 10 6"
      className="ml-2 block text-blue-500"
    >
      <path d="M5 0 10 6H0Z" fill="currentColor" />
    </svg>
    <div className="rounded-rg bg-blue-500 px-1.5 py-1 label-2-medium text-static-white shadow-medium">
      <p>워크북을 불러왔습니다.</p>
      <p>이제 IXD에 DSD 워크북의 값을 채워보세요.</p>
    </div>
  </div>
);

export const EditorSubHeader = ({
  filename,
  reportTypes,
  session,
  workbookName,
  hasWorkbook,
  workbookPending,
  showWorkbook,
  showWorkbookLoadedHint,
  collapsedPx = 0,
  onShowWorkbookChange,
  onWorkbooksSelect,
}: {
  /** 목록 API 에서 찾은 원본 파일명. 못 찾으면 페이지 이름으로 대신한다 */
  filename?: string;
  /** 이 문서가 담고 있는 재무제표 유형 — 유형마다 워크북을 하나씩 고르게 한다 */
  reportTypes: ReportType[];
  session: EditSession;
  /** 비교 중인 워크북 이름. 비교 데이터가 준비되기 전에는 없다 */
  workbookName?: string;
  /** 지금 보고 있는 유형에 워크북을 골라 뒀는지 */
  hasWorkbook: boolean;
  /** URL 로 워크북이 오는 중 — 곧 `워크북 변경` 이 되므로 안내를 시작하지 않는다 */
  workbookPending: boolean;
  showWorkbook: boolean;
  /** 워크북을 막 불러왔을 때 1회 안내 (Figma 7246:577434) */
  showWorkbookLoadedHint: boolean;
  /**
   * 지금 접혀 있는 양 (0 = 48px 전부 보임, 48 = 완전히 사라짐). 본문을 내린 만큼
   * 줄이 밀려 올라가고 올린 만큼 다시 드러난다.
   * ponytail: 언마운트가 아니라 높이를 줄이고 clip 한다. 떼어내면 `워크북 값
   * 불러오기` 안내의 노출 예산이 스크롤마다 깎이고, 1회 안내도 다시 뜬다.
   */
  collapsedPx?: number;
  onShowWorkbookChange: (show: boolean) => void;
  onWorkbooksSelect: (workbooks: DsdFile[]) => void;
}) => {
  // 편집기에 들어올 때마다 버튼 위치를 알린다 — 단 노출 예산(5회)이 남아 있을 때만.
  // 워크북이 이미 붙어 있으면(= 버튼이 `워크북 변경`) 기능을 찾은 것이니 띄우지도,
  // 예산을 쓰지도 않는다. 예산은 effect 에서 당긴다 — 초기화 함수에서 읽으면
  // StrictMode 의 이중 호출이 한 번을 더 까먹는다(`이전 파일 불러오기` 로 연 탭에서
  // 안내가 통째로 사라졌던 원인).
  const [showsWorkbookLoadGuide, setShowsWorkbookLoadGuide] = useState(false);
  const guideViewTakenRef = useRef(false);
  useEffect(() => {
    if (hasWorkbook) {
      // 안내 중에 워크북이 붙었으면 거둔다
      setShowsWorkbookLoadGuide(false);
      return;
    }
    if (workbookPending || guideViewTakenRef.current) return;
    guideViewTakenRef.current = true;
    if (takeWorkbookLoadGuideView()) setShowsWorkbookLoadGuide(true);
  }, [hasWorkbook, workbookPending]);

  // 줄이 접히기 시작하면 안내도 거둔다 — 앵커(`워크북 값 불러오기` 버튼)가 잘려
  // 올라가면 Radix popper 가 앵커 사각형을 잘못 읽어 말풍선을 뷰포트 왼쪽 위
  // 모서리로 옮겨 상단 헤더를 덮는다.
  useEffect(() => {
    if (collapsedPx > 0) setShowsWorkbookLoadGuide(false);
  }, [collapsedPx]);

  // 안내는 화면 아무 데나 클릭하면 사라진다. capture 로 받는다 — jspreadsheet 의
  // document 핸들러가 먼저 삼켜도 안내는 닫히게.
  useEffect(() => {
    if (!showsWorkbookLoadGuide) return;
    const close = () => setShowsWorkbookLoadGuide(false);
    document.addEventListener("pointerdown", close, true);
    return () => document.removeEventListener("pointerdown", close, true);
  }, [showsWorkbookLoadGuide]);
  const modifiedCount = Object.keys(session.cellChanges).length;
  const ixdFile: SubHeaderFile = {
    tag: workbookName && showWorkbook ? "IXD" : undefined,
    name: filename ?? "IXD 미리보기",
    addon:
      modifiedCount > 0 ? (
        <SubHeaderNote>
          {modifiedCount}개 수정. ‘저장’ 버튼을 눌러주세요.
        </SubHeaderNote>
      ) : undefined,
  };

  const loadWorkbookButton = (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      leftIcon={<FolderOpen className="w-3.5 h-3.5" />}
      onClick={() => {
        logIxdEditorAction(hasWorkbook ? "워크북 변경" : "워크북 값 불러오기");
        pushModal(WorkbookValueLoadModal, {
          reportTypes,
          // 모달은 고르고 스스로 닫는다 — 그 다음 일은 화면이 맡는다
          onConfirm: onWorkbooksSelect,
        });
      }}
      disabled={session.isActionLocked}
    >
      {hasWorkbook ? "워크북 변경" : "워크북 값 불러오기"}
    </Button>
  );

  const controls = (
    <>
      {hasWorkbook && (
        <div className="flex shrink-0 items-center gap-2">
          <span className="label-2-medium text-label-normal">
            DSD워크북 보기
          </span>
          <Switch
            size={24}
            color="blue"
            active={showWorkbook}
            onChange={(show) => {
              logIxdEditorAction(
                show ? "DSD워크북 보기 켜기" : "DSD워크북 보기 끄기",
              );
              onShowWorkbookChange(show);
            }}
            aria-label="DSD워크북 보기"
          />
        </div>
      )}
      <Tooltip
        label="워크북에서 태깅한 DSD 값을 IXD 파일로 손쉽게 옮기려면 해당 기능을 이용해 주세요."
        side="bottom"
        align="end"
        // 완전 제어 — 여는 것도 닫는 것도 이 화면이 정한다. Radix 의 hover 진입·이탈은
        // 쓰지 않는다(올렸다 떼면 사라지지 않게). 닫는 길은 화면 클릭 하나뿐이다.
        open={showsWorkbookLoadGuide}
        // 워크북을 막 불러왔을 때 뜨는 안내(WorkbookLoadedHint)와 같은 파랑
        contentClassName="max-w-[320px] bg-blue-500 break-keep"
        arrowClassName="fill-blue-500"
      >
        {loadWorkbookButton}
      </Tooltip>
    </>
  );

  // 두 칸일 때 스위치·버튼은 **DSD 워크북 칸 안**의 오른쪽 끝에 둔다.
  // 줄 전체의 actions 로 두면 그만큼 폭을 먹어 파일명 두 칸이 반씩 나뉘지 않고,
  // 본문의 좌(IXD)·우(DSD 워크북) 열과 제목이 어긋난다 (Figma 7160:519835).
  const isSplit = !!workbookName && showWorkbook;

  // 접힌 만큼 줄의 자리를 줄이고(= 본문이 그만큼 길어진다) 줄은 위로 밀어 올린다.
  // 바깥 박스가 잘라 주므로 `SubHeader` 는 48px 고정 그대로 둔다.
  //
  // 1회 안내(WorkbookLoadedHint)는 파일명 아래로 삐져나오는 절대 위치 요소라 두 가지를 지킨다.
  // - 자르기는 위쪽만 한다(overflow-hidden 이 아니라 clip-path). 아래까지 자르면 접히기
  //   시작하자마자 안내가 잘린다.
  // - 줄 자체를 z-[60] 으로 올린다. transform·clip-path 가 쌓임 맥락을 만들어 안내의
  //   z-[60] 이 줄 안에 갇히므로, 줄이 DOM 상 뒤인 본문(sticky z-50)보다 위여야 한다.
  const collapsing = collapsedPx > 0;
  return (
    <div
      data-testid="ixd-sub-header-row"
      className="relative z-[60] shrink-0"
      style={{
        height: SUB_HEADER_ROW_PX - collapsedPx,
        clipPath: collapsing ? "inset(0 -100vw -100vh -100vw)" : undefined,
      }}
    >
      <div style={{ transform: `translateY(${-collapsedPx}px)` }}>
        <SubHeader
          // 한 칸일 때도 파일명을 본문 카드 글자 시작점(px-4 16 + 테두리 1 + px-5 20)에 맞춘다.
          // 두 칸일 때는 칸마다 여백을 갖고 있어 줄에는 여백을 주지 않는다.
          className={cn(!isSplit && "pl-[37px]")}
          files={
            isSplit
              ? [
                  ixdFile,
                  {
                    tag: "DSD 워크북",
                    name: workbookName,
                    // 안내는 DSD 워크북 칸에만 붙는다 — "DSD워크북 보기"를 끈 동안에는
                    // 그 칸이 없어 뜨지 않는다 (가리킬 대상이 사라진다).
                    nameHint: showWorkbookLoadedHint ? (
                      <WorkbookLoadedHint />
                    ) : undefined,
                    actions: controls,
                  },
                ]
              : [ixdFile]
          }
          actions={isSplit ? undefined : controls}
        />
      </div>
    </div>
  );
};
